import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import '../styles/AdminPanel.css';
import AdminNavbar from '../components/AdminNavbar';
import styled from 'styled-components';
import ReactMarkdown from 'react-markdown';
import { toast } from 'react-hot-toast';

// Add the styled components
const StyledModal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background: white;
  border-radius: 12px;
  padding: 2rem;
  width: 90%;
  max-width: 500px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);

  h2 {
    margin: 0 0 1.5rem;
    font-size: 1.8rem;
    color: #333;
  }

  form {
    display: flex;
    flex-direction: column;
    gap: 1.2rem;
  }
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;

  label {
    font-weight: 500;
    color: #444;
    font-size: 0.95rem;
  }

  select, input[type="date"], input[type="text"] {
    padding: 0.75rem;
    border: 1px solid #ddd;
    border-radius: 8px;
    font-size: 1rem;
    transition: border-color 0.2s;

    &:focus {
      outline: none;
      border-color: #2563eb;
    }
  }

  &.checkbox-group {
    flex-direction: row;
    align-items: center;
    gap: 0.75rem;
    padding: 0.5rem 0;

    input[type="checkbox"] {
      width: 18px;
      height: 18px;
      accent-color: #2563eb;
    }
  }
`;

const ModalActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
  margin-top: 1.5rem;

  button {
    padding: 0.75rem 1.5rem;
    border-radius: 8px;
    font-size: 1rem;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s;

    &:first-child {
      background: #f3f4f6;
      border: none;
      color: #4b5563;

      &:hover {
        background: #e5e7eb;
      }
    }

    &:last-child {
      background: #2563eb;
      border: none;
      color: white;

      &:hover {
        background: #1d4ed8;
      }
    }
  }
`;

// Add new styled components for guides
const GuideEditor = styled.div`
  display: flex;
  gap: 2rem;
  height: calc(100vh - 200px);
`;

const EditorPane = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const PreviewPane = styled.div`
  flex: 1;
  padding: 1rem;
  background: white;
  border-radius: 8px;
  overflow-y: auto;
  border: 1px solid #ddd;

  .markdown-content {
    color: #333;
    line-height: 1.6;
    
    h1, h2, h3 {
      margin-top: 1.5rem;
      margin-bottom: 1rem;
    }

    ul, ol {
      margin-left: 1.5rem;
    }
  }
`;

const GuideList = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 1rem;
  margin-bottom: 2rem;
`;

const GuideCard = styled.div`
  background: white;
  border-radius: 8px;
  padding: 1rem;
  border: 1px solid #ddd;
  
  h3 {
    margin: 0 0 1rem;
    color: #333;
  }

  .actions {
    display: flex;
    gap: 0.5rem;
    margin-top: 1rem;
  }

  button {
    padding: 0.5rem 1rem;
    border-radius: 4px;
    border: none;
    cursor: pointer;
    font-size: 0.9rem;

    &.edit {
      background: #2563eb;
      color: white;
    }

    &.delete {
      background: #dc2626;
      color: white;
    }
  }
`;

// Add these styled components for feedback
const FeedbackContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 2rem;
  height: calc(100vh - 200px);
  
  @media (max-width: 1200px) {
    grid-template-columns: 1fr;
  }
`;

const FeedbackList = styled.div`
  background: white;
  border-radius: 12px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  overflow: hidden;
  display: flex;
  flex-direction: column;
`;

const FeedbackHeader = styled.div`
  padding: 1rem;
  border-bottom: 1px solid #eee;
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: #f8f9fa;
`;

const FeedbackFilters = styled.div`
  display: flex;
  gap: 1rem;
  padding: 1rem;
  border-bottom: 1px solid #eee;
  background: white;
`;

const FilterButton = styled.button`
  padding: 0.5rem 1rem;
  border: 1px solid #ddd;
  border-radius: 6px;
  background: ${props => props.active ? '#007bff' : 'white'};
  color: ${props => props.active ? 'white' : '#333'};
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: ${props => props.active ? '#0056b3' : '#f8f9fa'};
  }
`;

const FeedbackItems = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 1rem;
`;

const FeedbackCard = styled.div`
  background: ${props => props.selected ? '#f0f8ff' : 'white'};
  border: 1px solid ${props => props.selected ? '#007bff' : '#eee'};
  border-radius: 8px;
  padding: 1rem;
  margin-bottom: 1rem;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    border-color: #007bff;
    transform: translateY(-1px);
  }
`;

const FeedbackDetail = styled.div`
  background: white;
  border-radius: 12px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  padding: 1.5rem;
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const FeedbackMeta = styled.div`
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
  margin-top: 0.5rem;
`;

const MetaBadge = styled.span`
  padding: 0.25rem 0.75rem;
  border-radius: 12px;
  font-size: 0.875rem;
  background: ${props => {
    switch (props.type) {
      case 'bug': return '#fee2e2';
      case 'feature_request': return '#e0f2fe';
      case 'feedback': return '#f3e8ff';
      case 'open': return '#fef3c7';
      case 'in_progress': return '#dbeafe';
      case 'resolved': return '#dcfce7';
      case 'closed': return '#f3f4f6';
      default: return '#f3f4f6';
    }
  }};
  color: ${props => {
    switch (props.type) {
      case 'bug': return '#991b1b';
      case 'feature_request': return '#075985';
      case 'feedback': return '#6b21a8';
      case 'open': return '#92400e';
      case 'in_progress': return '#1e40af';
      case 'resolved': return '#166534';
      case 'closed': return '#374151';
      default: return '#374151';
    }
  }};
`;

const AdminPanel = () => {
  const [activeTab, setActiveTab] = useState('users');
  const [users, setUsers] = useState([]);
  const [admins, setAdmins] = useState([]);
  const [amazonUsers, setAmazonUsers] = useState([]);
  const [payments, setPayments] = useState([]);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showCreateUser, setShowCreateUser] = useState(false);
  const [newUser, setNewUser] = useState({
    username: '',
    expiry_date: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    plan_id: 'manual',
    manual_date: true,
    is_free: false,
    payment_method: '',
    payment_details: ''
  });
  const [showPaymentDetails, setShowPaymentDetails] = useState(false);
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [showCreateAdmin, setShowCreateAdmin] = useState(false);
  const [newAdmin, setNewAdmin] = useState({
    username: '',
    rank: 'manager' // Default to manager
  });
  const [createdAdminCredentials, setCreatedAdminCredentials] = useState(null);
  const [guides, setGuides] = useState([]);
  const [showGuideEditor, setShowGuideEditor] = useState(false);
  const [currentGuide, setCurrentGuide] = useState({
    id: '',
    title: '',
    content: '',
    platform: 'general'
  });
  const [feedback, setFeedback] = useState([]);
  const [selectedFeedback, setSelectedFeedback] = useState(null);
  const [adminReply, setAdminReply] = useState('');
  const [status, setStatus] = useState('open');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showCreateAmazonUser, setShowCreateAmazonUser] = useState(false);
  const [newAmazonUser, setNewAmazonUser] = useState({
    username: '',
    password: ''
  });
  const navigate = useNavigate();
  const adminRank = localStorage.getItem('adminRank');
  const [feedbackFilter, setFeedbackFilter] = useState('all');
  const [feedbackTypeFilter, setFeedbackTypeFilter] = useState('all');
  const [referrers, setReferrers] = useState([]);
  const [showReferrerModal, setShowReferrerModal] = useState(false);
  const [editingReferrer, setEditingReferrer] = useState(null);
  const [referrerForm, setReferrerForm] = useState({ id: '', name: '', logo_url: '', channel: '', description: '', password: '', percent: '' });

  // Add plans constant
  const plans = {
    'monthly': { days: 30, price: 250 },
    'quarterly': { days: 90, price: 500 },
    'lifetime': { days: 10950, price: 1000 }, // 30 years
    'manual': { days: 30, price: 0 }
  };

  // Add guides to the navItems array
  const defaultNavItems = [
    { id: 'dashboard', name: 'Dashboard' },
    { id: 'users', name: 'Users' },
    { id: 'payments', name: 'Payments' },
    { id: 'guides', name: 'Guides' },
    { id: 'feedback', name: 'Feedback' },
    { id: 'logs', name: 'Logs' }
  ];

  useEffect(() => {
    const token = localStorage.getItem('adminToken');
    if (!token) {
      navigate('/admin/login');
      return;
    }

    fetchData();
  }, [navigate]);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const headers = { Authorization: `Bearer ${token}` };

      const [usersResponse, adminsResponse, amazonUsersResponse, paymentsResponse, logsResponse, feedbackResponse] = await Promise.all([
        axios.get('/api/admin/users', { headers }),
        adminRank === 'owner' ? axios.get('/api/admin/admins', { headers }) : null,
        adminRank === 'owner' ? axios.get('/api/admin/amazon-users', { headers }) : null,
        adminRank === 'owner' ? axios.get('/api/admin/payments', { headers }) : null,
        adminRank === 'owner' ? axios.get('/api/admin/logs', { headers }) : null,
        axios.get('/api/admin/feedback', { headers })
      ]);

      setUsers(usersResponse.data.users);
      if (adminsResponse) setAdmins(adminsResponse.data.admins);
      if (amazonUsersResponse) setAmazonUsers(amazonUsersResponse.data.users);
      if (paymentsResponse) setPayments(paymentsResponse.data.payments);
      if (logsResponse) setLogs(logsResponse.data.logs);
      if (feedbackResponse) setFeedback(feedbackResponse.data.feedback || []);
      setLoading(false);
    } catch (err) {
      setError('Failed to fetch data');
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await axios.post('/api/admin/logout', {}, { withCredentials: true });
    } catch (e) {}
    localStorage.removeItem('adminRank');
    navigate('/admin/login');
  };

  const generateManualCredentials = () => {
    // Generate a random hex string for the license key
    const randomHex = Array.from(crypto.getRandomValues(new Uint8Array(8)))
      .map(b => b.toString(16).padStart(2, '0'))
      .join('')
      .toUpperCase();
    
    // Generate a random user ID (10 characters)
    const userId = Array.from(crypto.getRandomValues(new Uint8Array(5)))
      .map(b => b.toString(16).padStart(2, '0'))
      .join('')
      .toUpperCase();
    
    return {
      user_id: userId,
      access_code: `ACCTW-MAN-${randomHex}` // Manual account prefix
    };
  };

  const handlePlanChange = (e) => {
    const selectedPlan = e.target.value;
    const isManual = selectedPlan === 'manual';
    
    let expiryDate = new Date();
    if (!isManual) {
      expiryDate.setDate(expiryDate.getDate() + plans[selectedPlan].days);
    }

    setNewUser({
      ...newUser,
      plan_id: selectedPlan,
      manual_date: isManual,
      expiry_date: isManual ? newUser.expiry_date : expiryDate.toISOString().split('T')[0]
    });
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    try {
      const adminToken = localStorage.getItem('adminToken');
      
      // Generate credentials with manual designation
      const credentials = generateManualCredentials();
      
      // Use custom username if provided, otherwise use generated one
      const user_id = newUser.username || credentials.user_id;

      // Create user first
      const userResponse = await axios.post('/api/admin/users', {
        user_id,
        access_code: credentials.access_code,
        _action: 'manual_creation', // Add action flag for server logging
        expiry_date: newUser.expiry_date
      }, {
        headers: { Authorization: `Bearer ${adminToken}` }
      });

      if (userResponse.data.success) {
        // Create payment for the new user
        const paymentResponse = await axios.post('/api/admin/payments', {
          payment_id: `manual_${Date.now()}`,
          plan_id: newUser.plan_id,
          status: 'confirmed',
          amount: newUser.is_free ? 0 : plans[newUser.plan_id].price,
          currency: 'usd',
          license_key: credentials.access_code,
          expires_at: newUser.expiry_date,
          pay_address: !newUser.is_free ? `${newUser.payment_method}: ${newUser.payment_details}` : 'Free License',
          _action: 'manual_creation' // Add action flag for server logging
        }, {
          headers: { Authorization: `Bearer ${adminToken}` }
        });

        setShowCreateUser(false);
        setNewUser({
          username: '',
          expiry_date: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
          plan_id: 'manual',
          manual_date: true,
          is_free: false,
          payment_method: '',
          payment_details: ''
        });
        fetchData();
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to create user');
    }
  };

  const handleDeleteUser = async (userId) => {
    if (window.confirm('Are you sure you want to delete this user?')) {
      try {
        const token = localStorage.getItem('adminToken');
        const response = await axios.delete(`/api/admin/users/${userId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });

        if (response.data.success) {
          fetchData();
        }
      } catch (err) {
        setError(err.response?.data?.message || 'Failed to delete user');
      }
    }
  };

  const getPaymentStatus = (userId) => {
    const payment = payments.find(p => p.license_key === userId);
    if (!payment) return { status: 'No Payment', class: 'status-expired' };
    if (new Date(payment.expires_at) < new Date()) return { status: 'Expired', class: 'status-expired' };
    return { status: 'Active', class: 'status-confirmed' };
  };

  const generateSecurePassword = () => {
    // Generate a password that includes uppercase, lowercase, numbers, and special characters
    const length = 16;
    const charset = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%^&*()_+-=';
    let password = '';
    
    // Ensure at least one of each type
    password += 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'[Math.floor(Math.random() * 26)]; // uppercase
    password += 'abcdefghijklmnopqrstuvwxyz'[Math.floor(Math.random() * 26)]; // lowercase
    password += '0123456789'[Math.floor(Math.random() * 10)]; // number
    password += '!@#$%^&*()_+-='[Math.floor(Math.random() * 14)]; // special
    
    // Fill the rest randomly
    for (let i = password.length; i < length; i++) {
      password += charset[Math.floor(Math.random() * charset.length)];
    }
    
    // Shuffle the password
    return password.split('').sort(() => Math.random() - 0.5).join('');
  };

  const handleCreateAdmin = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('adminToken');
      const password = generateSecurePassword();
      
      const response = await axios.post('/api/admin/create-admin', {
        ...newAdmin,
        password
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        // Store the credentials to show in the success modal
        setCreatedAdminCredentials({
          username: newAdmin.username,
          password: password,
          rank: newAdmin.rank
        });
        
        // Reset the form
        setNewAdmin({
          username: '',
          rank: 'manager'
        });
        setShowCreateAdmin(false);
        
        // Optionally refresh the logs to show the new admin creation
        if (activeTab === 'logs') {
          fetchData();
        }
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to create admin account');
    }
  };

  // Add guides fetching
  useEffect(() => {
    if (activeTab === 'guides') {
      fetchGuides();
    }
  }, [activeTab]);

  const fetchGuides = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await axios.get('/api/admin/guides', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setGuides(response.data.guides);
    } catch (err) {
      console.error('Error fetching guides:', err);
    }
  };

  const handleSaveGuide = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const method = currentGuide.id ? 'put' : 'post';
      const url = currentGuide.id 
        ? `/api/admin/guides/${currentGuide.id}`
        : '/api/admin/guides';

      await axios[method](url, currentGuide, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setShowGuideEditor(false);
      setCurrentGuide({ id: '', title: '', content: '', platform: 'general' });
      fetchGuides();
    } catch (err) {
      console.error('Error saving guide:', err);
    }
  };

  const handleDeleteGuide = async (guideId) => {
    if (window.confirm('Are you sure you want to delete this guide?')) {
      try {
        const token = localStorage.getItem('adminToken');
        await axios.delete(`/api/admin/guides/${guideId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        fetchGuides();
      } catch (err) {
        console.error('Error deleting guide:', err);
      }
    }
  };

  // Add useEffect to load feedback when the tab is active
  useEffect(() => {
    if (activeTab === 'feedback') {
      loadFeedback();
    }
  }, [activeTab]);

  // Add function to load feedback
  const loadFeedback = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await axios.get('/api/admin/feedback', {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        setFeedback(response.data.feedback);
      }
    } catch (error) {
      console.error('Error loading feedback:', error);
      toast.error('Failed to load feedback');
    }
  };

  // Add function to handle feedback status update
  const handleStatusUpdate = async () => {
    if (!selectedFeedback) return;

    setIsSubmitting(true);
    try {
      const token = localStorage.getItem('adminToken');
      const res = await axios.put(
        `/api/admin/feedback/${selectedFeedback.id}`,
        {
          status,
          response: adminReply || null,
          notify: true // Add this flag to indicate we want to notify the user
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      if (res.data.success) {
        toast.success('Feedback updated successfully');
        setSelectedFeedback(null);
        setAdminReply('');
        setStatus('open');
        loadFeedback();
      }
    } catch (error) {
      console.error('Error updating feedback:', error);
      toast.error('Failed to update feedback');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCreateAmazonUser = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('adminToken');
      const response = await axios.post('/api/admin/amazon-users', newAmazonUser, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        setShowCreateAmazonUser(false);
        setNewAmazonUser({ username: '', password: '' });
        toast.success('Amazon user account created successfully');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to create Amazon user account');
    }
  };

  const fetchReferrers = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await axios.get('/api/admin/referrers', { headers: { Authorization: `Bearer ${token}` } });
      if (response.data.success) setReferrers(response.data.referrers);
    } catch (err) {
      toast.error('Failed to fetch referrers');
    }
  };

  useEffect(() => {
    if (activeTab === 'referrers') fetchReferrers();
  }, [activeTab]);

  const handleReferrerFormChange = (e) => {
    setReferrerForm({ ...referrerForm, [e.target.name]: e.target.value });
  };

  const handleReferrerSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('adminToken');
      if (editingReferrer) {
        await axios.put(`/api/admin/referrers/${editingReferrer.id}`, referrerForm, { headers: { Authorization: `Bearer ${token}` } });
        toast.success('Referrer updated');
      } else {
        await axios.post('/api/admin/referrers', referrerForm, { headers: { Authorization: `Bearer ${token}` } });
        toast.success('Referrer created');
      }
      setShowReferrerModal(false);
      setEditingReferrer(null);
      setReferrerForm({ id: '', name: '', logo_url: '', channel: '', description: '', password: '', percent: '' });
      fetchReferrers();
    } catch (err) {
      toast.error('Failed to save referrer');
    }
  };

  const handleEditReferrer = (ref) => {
    setEditingReferrer(ref);
    setReferrerForm(ref);
    setShowReferrerModal(true);
  };

  const handleDeleteReferrer = async (id) => {
    if (!window.confirm('Delete this referrer?')) return;
    try {
      const token = localStorage.getItem('adminToken');
      await axios.delete(`/api/admin/referrers/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Referrer deleted');
      fetchReferrers();
    } catch (err) {
      toast.error('Failed to delete referrer');
    }
  };

  const renderFeedbackContent = () => {
    const filteredFeedback = feedback.filter(item => {
      const statusMatch = feedbackFilter === 'all' || item.status === feedbackFilter;
      const typeMatch = feedbackTypeFilter === 'all' || item.type === feedbackTypeFilter;
      return statusMatch && typeMatch;
    });

    return (
      <FeedbackContainer>
        <FeedbackList>
          <FeedbackHeader>
            <h3>Feedback List</h3>
            <span>{filteredFeedback.length} items</span>
          </FeedbackHeader>
          
          <FeedbackFilters>
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <FilterButton 
                active={feedbackFilter === 'all'} 
                onClick={() => setFeedbackFilter('all')}
              >
                All
              </FilterButton>
              <FilterButton 
                active={feedbackFilter === 'open'} 
                onClick={() => setFeedbackFilter('open')}
              >
                Open
              </FilterButton>
              <FilterButton 
                active={feedbackFilter === 'in_progress'} 
                onClick={() => setFeedbackFilter('in_progress')}
              >
                In Progress
              </FilterButton>
              <FilterButton 
                active={feedbackFilter === 'resolved'} 
                onClick={() => setFeedbackFilter('resolved')}
              >
                Resolved
              </FilterButton>
              <FilterButton 
                active={feedbackFilter === 'closed'} 
                onClick={() => setFeedbackFilter('closed')}
              >
                Closed
              </FilterButton>
            </div>
            
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <FilterButton 
                active={feedbackTypeFilter === 'all'} 
                onClick={() => setFeedbackTypeFilter('all')}
              >
                All Types
              </FilterButton>
              <FilterButton 
                active={feedbackTypeFilter === 'bug'} 
                onClick={() => setFeedbackTypeFilter('bug')}
              >
                Bugs
              </FilterButton>
              <FilterButton 
                active={feedbackTypeFilter === 'feature_request'} 
                onClick={() => setFeedbackTypeFilter('feature_request')}
              >
                Features
              </FilterButton>
              <FilterButton 
                active={feedbackTypeFilter === 'feedback'} 
                onClick={() => setFeedbackTypeFilter('feedback')}
              >
                Feedback
              </FilterButton>
            </div>
          </FeedbackFilters>

          <FeedbackItems>
            {filteredFeedback.map(item => (
              <FeedbackCard
                key={item.id}
                selected={selectedFeedback?.id === item.id}
                onClick={() => {
                  setSelectedFeedback(item);
                  setAdminReply(item.response || '');
                  setStatus(item.status);
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <span style={{ fontSize: '1.2rem' }}>
                    {item.type === 'feedback' ? '💬' : item.type === 'bug' ? '🐛' : '✨'}
                  </span>
                  <strong>{item.title}</strong>
                </div>
                <p style={{ color: '#666', margin: '0.5rem 0' }}>
                  {item.description.length > 100 ? item.description.slice(0, 100) + '...' : item.description}
                </p>
                <FeedbackMeta>
                  <MetaBadge type={item.type}>
                    {item.type.replace('_', ' ')}
                  </MetaBadge>
                  <MetaBadge type={item.status}>
                    {item.status.replace('_', ' ')}
                  </MetaBadge>
                  {item.platform && (
                    <MetaBadge>
                      {item.platform}
                    </MetaBadge>
                  )}
                  {item.version && (
                    <MetaBadge>
                      v{item.version}
                    </MetaBadge>
                  )}
                </FeedbackMeta>
                <div style={{ fontSize: '0.875rem', color: '#666', marginTop: '0.5rem' }}>
                  {new Date(item.created_at).toLocaleString()}
                </div>
              </FeedbackCard>
            ))}
          </FeedbackItems>
        </FeedbackList>

        <FeedbackDetail>
          {selectedFeedback ? (
            <>
              <div>
                <h3>{selectedFeedback.title}</h3>
                <FeedbackMeta>
                  <MetaBadge type={selectedFeedback.type}>
                    {selectedFeedback.type.replace('_', ' ')}
                  </MetaBadge>
                  <MetaBadge type={selectedFeedback.status}>
                    {selectedFeedback.status.replace('_', ' ')}
                  </MetaBadge>
                  {selectedFeedback.platform && (
                    <MetaBadge>
                      {selectedFeedback.platform}
                    </MetaBadge>
                  )}
                  {selectedFeedback.version && (
                    <MetaBadge>
                      v{selectedFeedback.version}
                    </MetaBadge>
                  )}
                </FeedbackMeta>
              </div>
              {selectedFeedback.user_id && (
                <div>
                  <h4>User ID</h4>
                  <code>{selectedFeedback.user_id}</code>
                </div>
              )}
              <div>
                <h4>Description</h4>
                <p style={{ whiteSpace: 'pre-wrap' }}>{selectedFeedback.description}</p>
              </div>

              {selectedFeedback.telegram && (
                <div>
                  <h4>Contact</h4>
                  <p>Telegram: {selectedFeedback.telegram}</p>
                </div>
              )}

              <div>
                <h4>Status</h4>
                <select
                  value={status}
                  onChange={e => setStatus(e.target.value)}
                  style={{ width: '100%', padding: '0.5rem', borderRadius: '6px', border: '1px solid #ddd' }}
                >
                  <option value="open">Open</option>
                  <option value="in_progress">In Progress</option>
                  <option value="resolved">Resolved</option>
                  <option value="closed">Closed</option>
                </select>
              </div>

              <div>
                <h4>Response</h4>
                <textarea
                  value={adminReply}
                  onChange={e => setAdminReply(e.target.value)}
                  style={{
                    width: '100%',
                    minHeight: '100px',
                    padding: '0.75rem',
                    borderRadius: '6px',
                    border: '1px solid #ddd',
                    resize: 'vertical'
                  }}
                  placeholder="Enter your response..."
                />
              </div>

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '1rem' }}>
                <button
                  onClick={() => {
                    setSelectedFeedback(null);
                    setAdminReply('');
                    setStatus('open');
                  }}
                  style={{
                    padding: '0.75rem 1.5rem',
                    borderRadius: '6px',
                    border: '1px solid #ddd',
                    background: 'white',
                    cursor: 'pointer'
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={handleStatusUpdate}
                  disabled={isSubmitting}
                  style={{
                    padding: '0.75rem 1.5rem',
                    borderRadius: '6px',
                    border: 'none',
                    background: '#007bff',
                    color: 'white',
                    cursor: isSubmitting ? 'not-allowed' : 'pointer',
                    opacity: isSubmitting ? 0.7 : 1
                  }}
                >
                  {isSubmitting ? 'Updating...' : 'Update Feedback'}
                </button>
              </div>
            </>
          ) : (
            <div style={{ 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center', 
              height: '100%',
              color: '#666',
              textAlign: 'center'
            }}>
              Select a feedback item to view details
            </div>
          )}
        </FeedbackDetail>
      </FeedbackContainer>
    );
  };

  const renderContent = () => {
    switch (activeTab) {
      case 'users':
        return (
          <section>
            <div className="section-header">
              <h2>Users</h2>
              <button 
                onClick={() => setShowCreateUser(true)}
                className="action-button"
              >
                Create User
              </button>
            </div>

            {showCreateUser && (
              <StyledModal>
                <ModalContent>
                  <h2>Create New User</h2>
                  <form onSubmit={handleCreateUser}>
                    <FormGroup>
                      <label>Username (Optional)</label>
                      <input
                        type="text"
                        value={newUser.username}
                        onChange={(e) => setNewUser({ ...newUser, username: e.target.value })}
                        placeholder="Leave blank for auto-generated username"
                      />
                    </FormGroup>
                    <FormGroup>
                      <label>Plan</label>
                      <select
                        value={newUser.plan_id}
                        onChange={handlePlanChange}
                        required
                      >
                        <option value="manual">Manual (Custom Date)</option>
                        <option value="monthly">Monthly (30 days)</option>
                        <option value="quarterly">Quarterly (90 days)</option>
                        <option value="lifetime">Lifetime (30 years)</option>
                      </select>
                    </FormGroup>
                    
                    {newUser.manual_date && (
                      <FormGroup>
                        <label>Expiry Date</label>
                        <input
                          type="date"
                          value={newUser.expiry_date}
                          onChange={(e) => setNewUser({ ...newUser, expiry_date: e.target.value })}
                          required
                        />
                      </FormGroup>
                    )}
                    
                    <FormGroup className="checkbox-group">
                      <input
                        type="checkbox"
                        id="free-license"
                        checked={newUser.is_free}
                        onChange={(e) => setNewUser({ ...newUser, is_free: e.target.checked })}
                      />
                      <label htmlFor="free-license">Free License</label>
                    </FormGroup>
                    
                    {!newUser.is_free && !newUser.manual_date && (
                      <>
                        <FormGroup>
                          <label>Payment Method</label>
                          <select
                            value={newUser.payment_method}
                            onChange={(e) => setNewUser({ ...newUser, payment_method: e.target.value })}
                            required
                          >
                            <option value="">Select Payment Method</option>
                            <option value="crypto">Crypto</option>
                            <option value="wise">Wise</option>
                            <option value="paypal">PayPal</option>
                            <option value="ukbt">UK Bank Transfer</option>
                            <option value="other">Other</option>
                          </select>
                        </FormGroup>
                        
                        <FormGroup>
                          <label>Payment Details</label>
                          <input
                            type="text"
                            value={newUser.payment_details}
                            onChange={(e) => setNewUser({ ...newUser, payment_details: e.target.value })}
                            placeholder={`Enter ${newUser.payment_method} transaction details`}
                            required
                          />
                        </FormGroup>
                      </>
                    )}
                    
                    <ModalActions>
                      <button type="button" onClick={() => setShowCreateUser(false)}>
                        Cancel
                      </button>
                      <button type="submit">
                        Create
                      </button>
                    </ModalActions>
                  </form>
                </ModalContent>
              </StyledModal>
            )}

            <table className="data-table">
              <thead>
                <tr>
                  <th>User ID</th>
                  <th>Access Code</th>
                  <th>Payment ID</th>
                  <th>Created</th>
                  <th>Status</th>
                  <th>Plan</th>
                  <th>Amount</th>
                  <th>Expires</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map(user => (
                  <tr key={user.user_id}>
                    <td data-label="User ID">{user.user_id}</td>
                    <td data-label="Access Code">
                      <code className="access-code">{user.access_code}</code>
                    </td>
                    <td data-label="Payment ID">
                      <code className="payment-id">{user.payment_id || 'N/A'}</code>
                    </td>
                    <td data-label="Created">{new Date(user.created_at).toLocaleDateString()}</td>
                    <td data-label="Status">
                      <span className={`status-badge ${
                        user.payment_status === 'confirmed' || user.payment_status === 'finished' || user.payment_status === 'early_confirmed' ? 'status-confirmed' :
                        user.payment_status === 'No Payment' ? 'status-no-payment' :
                        'status-pending'
                      }`}>
                        {user.payment_status === 'early_confirmed' ? 'confirmed' : user.payment_status}
                      </span>
                    </td>
                    <td data-label="Plan">
                      <span className="plan-badge">
                        {user.plan_type}
                      </span>
                    </td>
                    <td data-label="Amount">
                      <div className="amount-cell">
                        <span className="amount">{user.amount}</span>
                        <span className="currency">{user.currency}</span>
                      </div>
                    </td>
                    <td data-label="Expires">
                      {user.expires_at ? 
                        new Date(user.expires_at).toLocaleDateString() : 
                        'N/A'
                      }
                    </td>
                    <td data-label="Actions" className="actions-cell">
                      <button 
                        className="action-button delete-button"
                        onClick={() => handleDeleteUser(user.user_id)}
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>
        );

      case 'admins':
        return adminRank === 'owner' ? (
          <section>
            <div className="section-header">
              <h2>Admin Accounts</h2>
              <button 
                onClick={() => setShowCreateAdmin(true)}
                className="action-button"
              >
                Create Admin
              </button>
            </div>
            <table className="data-table">
              <thead>
                <tr>
                  <th>Username</th>
                  <th>Rank</th>
                  <th>Created At</th>
                  <th>Last Login</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {admins.map(admin => (
                  <tr key={admin.id}>
                    <td>{admin.username}</td>
                    <td>
                      <span className={`rank-badge rank-${admin.rank}`}>
                        {admin.rank}
                      </span>
                    </td>
                    <td>{new Date(admin.created_at).toLocaleString()}</td>
                    <td>{admin.last_login ? new Date(admin.last_login).toLocaleString() : 'Never'}</td>
                    <td>
                      <button 
                        className="action-button delete-button"
                        onClick={() => handleDeleteAdmin(admin.id)}
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>
        ) : null;

      case 'amazon':
        return adminRank === 'owner' ? (
          <section>
            <div className="section-header">
              <h2>Amazon Users</h2>
              <button 
                onClick={() => setShowCreateAmazonUser(true)}
                className="action-button"
              >
                Create Amazon User
              </button>
            </div>
            {showCreateAmazonUser && (
              <StyledModal>
                <ModalContent>
                  <h2>Create Amazon User Account</h2>
                  <form onSubmit={handleCreateAmazonUser}>
                    <FormGroup>
                      <label>Username</label>
                      <input
                        type="text"
                        value={newAmazonUser.username}
                        onChange={(e) => setNewAmazonUser({ ...newAmazonUser, username: e.target.value })}
                        required
                        placeholder="Enter username"
                      />
                    </FormGroup>
                    <FormGroup>
                      <label>Password</label>
                      <input
                        type="password"
                        value={newAmazonUser.password}
                        onChange={(e) => setNewAmazonUser({ ...newAmazonUser, password: e.target.value })}
                        required
                        placeholder="Enter password"
                      />
                    </FormGroup>
                    <ModalActions>
                      <button type="button" onClick={() => setShowCreateAmazonUser(false)}>
                        Cancel
                      </button>
                      <button type="submit">
                        Create Account
                      </button>
                    </ModalActions>
                  </form>
                </ModalContent>
              </StyledModal>
            )}
            <table className="data-table">
              <thead>
                <tr>
                  <th>Username</th>
                  <th>Created At</th>
                  <th>Last Login</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {amazonUsers.map(user => (
                  <tr key={user.id}>
                    <td>{user.username}</td>
                    <td>{new Date(user.created_at).toLocaleString()}</td>
                    <td>{user.last_login ? new Date(user.last_login).toLocaleString() : 'Never'}</td>
                    <td>
                      <button 
                        className="action-button delete-button"
                        onClick={() => handleDeleteAmazonUser(user.id)}
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>
        ) : null;

      case 'payments':
        return adminRank === 'owner' ? (
          <section>
            <div className="section-header">
              <h2>Payments</h2>
            </div>
            <div className="table-container">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Payment ID</th>
                    <th>License Key</th>
                    <th>Plan</th>
                    <th>Status</th>
                    <th>Amount</th>
                    <th>Crypto</th>
                    <th>Network</th>
                    <th>Transaction</th>
                    <th>Created</th>
                    <th>Expires</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {payments.map(payment => (
                    <tr key={payment.payment_id}>
                      <td data-label="Payment ID">
                        <code className="payment-id">{payment.payment_id}</code>
                      </td>
                      <td data-label="License Key">
                        <code className="license-key">{payment.license_key || 'N/A'}</code>
                      </td>
                      <td data-label="Plan">
                        <span className="plan-badge">
                          {payment.plan_id}
                        </span>
                      </td>
                      <td data-label="Status">
                        <span className={`status-badge ${
                          payment.status === 'confirmed' || payment.status === 'finished' ? 'status-confirmed' :
                          payment.status === 'early_confirmed' ? 'status-confirmed' :
                          payment.status === 'waiting' ? 'status-pending' :
                          'status-expired'
                        }`}>
                          {payment.status === 'early_confirmed' ? 'confirmed' : payment.status}
                        </span>
                      </td>
                      <td data-label="Amount">
                        <div className="amount-cell">
                          <span className="amount">{payment.amount}</span>
                          <span className="currency">{payment.currency}</span>
                        </div>
                      </td>
                      <td data-label="Crypto">
                        {payment.crypto_amount ? (
                          <div className="crypto-cell">
                            <span className="amount">{payment.crypto_amount}</span>
                            <span className="currency">{payment.crypto_currency}</span>
                          </div>
                        ) : 'N/A'}
                      </td>
                      <td data-label="Network">
                        <span className="network-badge">
                          {payment.network || 'N/A'}
                        </span>
                      </td>
                      <td data-label="Transaction">
                        {payment.payin_hash ? (
                          <div className="hash-cell">
                            <code className="hash" title={payment.payin_hash}>
                              {payment.payin_hash.substring(0, 8)}...
                            </code>
                            {payment.payout_hash && (
                              <code className="hash" title={payment.payout_hash}>
                                {payment.payout_hash.substring(0, 8)}...
                              </code>
                            )}
                          </div>
                        ) : 'N/A'}
                      </td>
                      <td data-label="Created">
                        {new Date(payment.created_at).toLocaleDateString()}
                      </td>
                      <td data-label="Expires">
                        {payment.expires_at ? 
                          new Date(payment.expires_at).toLocaleDateString() : 
                          'N/A'
                        }
                      </td>
                      <td data-label="Actions" className="actions-cell">
                        <button 
                          className="action-button view-button"
                          onClick={() => {
                            setSelectedPayment(payment);
                            setShowPaymentDetails(true);
                          }}
                        >
                          View Details
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Payment Details Modal */}
            {showPaymentDetails && selectedPayment && (
              <div className="modal">
                <div className="modal-content payment-details-modal">
                  <h2>Payment Details</h2>
                  <div className="payment-details-grid">
                    <div className="detail-group">
                      <h3>Basic Information</h3>
                      <div className="detail-item">
                        <span className="detail-label">Payment ID:</span>
                        <code>{selectedPayment.payment_id}</code>
                      </div>
                      <div className="detail-item">
                        <span className="detail-label">Order ID:</span>
                        <code>{selectedPayment.order_id || 'N/A'}</code>
                      </div>
                      <div className="detail-item">
                        <span className="detail-label">Plan:</span>
                        <span className="plan-badge">{selectedPayment.plan_id}</span>
                      </div>
                      <div className="detail-item">
                        <span className="detail-label">Status:</span>
                        <span className={`status-badge status-${selectedPayment.status}`}>
                          {selectedPayment.status}
                        </span>
                      </div>
                    </div>

                    <div className="detail-group">
                      <h3>Payment Information</h3>
                      <div className="detail-item">
                        <span className="detail-label">Amount:</span>
                        <span>{selectedPayment.amount} {selectedPayment.currency}</span>
                      </div>
                      <div className="detail-item">
                        <span className="detail-label">Crypto Amount:</span>
                        <span>
                          {selectedPayment.crypto_amount ? 
                            `${selectedPayment.crypto_amount} ${selectedPayment.crypto_currency}` : 
                            'N/A'
                          }
                        </span>
                      </div>
                      <div className="detail-item">
                        <span className="detail-label">Network:</span>
                        <span className="network-badge">{selectedPayment.network || 'N/A'}</span>
                      </div>
                      <div className="detail-item">
                        <span className="detail-label">Pay Address:</span>
                        <code>{selectedPayment.pay_address || 'N/A'}</code>
                      </div>
                    </div>

                    <div className="detail-group">
                      <h3>Transaction Details</h3>
                      <div className="detail-item">
                        <span className="detail-label">Pay-in Hash:</span>
                        <code className="hash">{selectedPayment.payin_hash || 'N/A'}</code>
                      </div>
                      <div className="detail-item">
                        <span className="detail-label">Payout Hash:</span>
                        <code className="hash">{selectedPayment.payout_hash || 'N/A'}</code>
                      </div>
                      <div className="detail-item">
                        <span className="detail-label">Actually Paid:</span>
                        <span>{selectedPayment.actually_paid || 'N/A'}</span>
                      </div>
                      <div className="detail-item">
                        <span className="detail-label">Purchase ID:</span>
                        <code>{selectedPayment.purchase_id || 'N/A'}</code>
                      </div>
                    </div>

                    <div className="detail-group">
                      <h3>Additional Information</h3>
                      <div className="detail-item">
                        <span className="detail-label">Created:</span>
                        <span>{new Date(selectedPayment.created_at).toLocaleString()}</span>
                      </div>
                      <div className="detail-item">
                        <span className="detail-label">Updated:</span>
                        <span>{new Date(selectedPayment.updated_at).toLocaleString()}</span>
                      </div>
                      <div className="detail-item">
                        <span className="detail-label">Expires:</span>
                        <span>
                          {selectedPayment.expires_at ? 
                            new Date(selectedPayment.expires_at).toLocaleString() : 
                            'N/A'
                          }
                        </span>
                      </div>
                      <div className="detail-item">
                        <span className="detail-label">Credentials Viewed:</span>
                        <span>{selectedPayment.credentials_viewed ? 'Yes' : 'No'}</span>
                      </div>
                    </div>
                  </div>

                  <div className="modal-actions">
                    {selectedPayment.payment_url && (
                      <a 
                        href={selectedPayment.payment_url} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="payment-link-button"
                      >
                        Open Payment Page
                      </a>
                    )}
                    <button onClick={() => setShowPaymentDetails(false)}>Close</button>
                  </div>
                </div>
              </div>
            )}
          </section>
        ) : null;

      case 'guides':
        return adminRank === 'owner' ? (
          <section>
            <div className="section-header">
              <h2>Guides</h2>
              <button 
                onClick={() => {
                  setCurrentGuide({ id: '', title: '', content: '', platform: 'general' });
                  setShowGuideEditor(true);
                }}
                className="action-button"
              >
                Create New Guide
              </button>
            </div>

            {showGuideEditor ? (
              <GuideEditor>
                <EditorPane>
                  <FormGroup>
                    <label>Title</label>
                    <input
                      type="text"
                      value={currentGuide.title}
                      onChange={(e) => setCurrentGuide({ ...currentGuide, title: e.target.value })}
                      placeholder="Guide Title"
                    />
                  </FormGroup>
                  
                  <FormGroup>
                    <label>Platform</label>
                    <select
                      value={currentGuide.platform}
                      onChange={(e) => setCurrentGuide({ ...currentGuide, platform: e.target.value })}
                    >
                      <option value="general">General</option>
                      <option value="amazon">Amazon</option>
                      <option value="ebay">eBay</option>
                      <option value="vinted">Vinted</option>
                    </select>
                  </FormGroup>
                  
                  <FormGroup>
                    <label>Content (Markdown)</label>
                    <textarea
                      value={currentGuide.content}
                      onChange={(e) => setCurrentGuide({ ...currentGuide, content: e.target.value })}
                      placeholder="Write your guide content in Markdown..."
                      style={{ height: '400px' }}
                    />
                  </FormGroup>
                  
                  <ModalActions>
                    <button onClick={() => setShowGuideEditor(false)}>Cancel</button>
                    <button onClick={handleSaveGuide}>Save Guide</button>
                  </ModalActions>
                </EditorPane>
                
                <PreviewPane>
                  <h2>Preview</h2>
                  <div className="markdown-content">
                    <ReactMarkdown>{currentGuide.content}</ReactMarkdown>
                  </div>
                </PreviewPane>
              </GuideEditor>
            ) : (
              <GuideList>
                {guides.map(guide => (
                  <GuideCard key={guide.id}>
                    <h3>{guide.title}</h3>
                    <div>Platform: {guide.platform}</div>
                    <div className="actions">
                      <button 
                        className="edit"
                        onClick={() => {
                          setCurrentGuide(guide);
                          setShowGuideEditor(true);
                        }}
                      >
                        Edit
                      </button>
                      <button 
                        className="delete"
                        onClick={() => handleDeleteGuide(guide.id)}
                      >
                        Delete
                      </button>
                    </div>
                  </GuideCard>
                ))}
              </GuideList>
            )}
          </section>
        ) : (
          <div className="unauthorized-message">
            Only owners can access the guides management section.
          </div>
        );

      case 'referrers':
        return adminRank === 'owner' ? (
          <section>
            <div className="section-header">
              <h2>Referrers</h2>
              <button className="action-button" onClick={() => { setShowReferrerModal(true); setEditingReferrer(null); setReferrerForm({ id: '', name: '', logo_url: '', channel: '', description: '', password: '', percent: '' }); }}>Add Referrer</button>
            </div>
            <table className="data-table">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Name</th>
                  <th>Logo</th>
                  <th>Channel</th>
                  <th>Description</th>
                  <th>Commission %</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {referrers.map(ref => (
                  <tr key={ref.id}>
                    <td>{ref.id}</td>
                    <td>{ref.name}</td>
                    <td>{ref.logo_url ? <img src={ref.logo_url} alt="logo" style={{ width: 40, height: 40, borderRadius: '50%' }} /> : ''}</td>
                    <td>{ref.channel}</td>
                    <td>{ref.description}</td>
                    <td>{ref.percent}%</td>
                    <td>
                      <button className="action-button" onClick={() => handleEditReferrer(ref)}>Edit</button>
                      <button className="action-button delete-button" onClick={() => handleDeleteReferrer(ref.id)}>Delete</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {showReferrerModal && (
              <StyledModal>
                <ModalContent>
                  <h2>{editingReferrer ? 'Edit Referrer' : 'Add Referrer'}</h2>
                  <form onSubmit={handleReferrerSubmit}>
                    <FormGroup>
                      <label>ID (URL slug, unique)</label>
                      <input name="id" value={referrerForm.id} onChange={handleReferrerFormChange} required disabled={!!editingReferrer} />
                    </FormGroup>
                    <FormGroup>
                      <label>Name</label>
                      <input name="name" value={referrerForm.name} onChange={handleReferrerFormChange} required />
                    </FormGroup>
                    <FormGroup>
                      <label>Logo URL</label>
                      <input name="logo_url" value={referrerForm.logo_url} onChange={handleReferrerFormChange} />
                    </FormGroup>
                    <FormGroup>
                      <label>Channel</label>
                      <input name="channel" value={referrerForm.channel} onChange={handleReferrerFormChange} />
                    </FormGroup>
                    <FormGroup>
                      <label>Description</label>
                      <textarea name="description" value={referrerForm.description} onChange={handleReferrerFormChange} />
                    </FormGroup>
                    <FormGroup>
                      <label>Password</label>
                      <input name="password" type="password" value={referrerForm.password || ''} onChange={handleReferrerFormChange} placeholder="Set or change password (leave blank to keep current)" style={{ padding: '0.75rem', border: '1px solid #ddd', borderRadius: 8, fontSize: '1rem' }} />
                    </FormGroup>
                    <FormGroup>
                      <label>Commission %</label>
                      <input name="percent" type="number" min="0" max="100" step="0.01" value={referrerForm.percent || ''} onChange={handleReferrerFormChange} placeholder="e.g. 20 for 20%" style={{ padding: '0.75rem', border: '1px solid #ddd', borderRadius: 8, fontSize: '1rem' }} />
                    </FormGroup>
                    <ModalActions>
                      <button type="button" onClick={() => { setShowReferrerModal(false); setEditingReferrer(null); }}>Cancel</button>
                      <button type="submit">Save</button>
                    </ModalActions>
                  </form>
                </ModalContent>
              </StyledModal>
            )}
          </section>
        ) : (
          <div className="unauthorized-message">Only owners can manage referrers.</div>
        );

      case 'feedback':
        return adminRank === 'owner' || adminRank === 'manager' ? (
          <section>
            <div className="section-header">
              <h2>Feedback Management</h2>
            </div>
            {renderFeedbackContent()}
          </section>
        ) : (
          <div className="unauthorized-message">
            You need to be a manager or owner to access the feedback management section.
          </div>
        );

      case 'logs':
        return adminRank === 'owner' ? (
          <section>
            <div className="section-header">
              <h2>Logs</h2>
            </div>
            <table className="data-table">
              <thead>
                <tr>
                  <th>Time</th>
                  <th>Admin</th>
                  <th>Rank</th>
                  <th>Action</th>
                  <th>Details</th>
                </tr>
              </thead>
              <tbody>
                {logs.map(log => (
                  <tr key={log.id} className="log-entry">
                    <td className="log-timestamp">
                      {new Date(log.created_at).toLocaleString()}
                    </td>
                    <td className="log-admin">
                      {log.admin}
                    </td>
                    <td>
                      <span className={`rank-badge rank-${log.admin_rank}`}>
                        {log.admin_rank}
                      </span>
                    </td>
                    <td className="log-action">
                      {log.action.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                    </td>
                    <td className="log-details">
                      <div className="details-grid">
                        {Object.entries(log.details).map(([key, value]) => (
                          <div key={key} className="detail-item">
                            <span className="detail-key">{key.replace(/_/g, ' ')}:</span>
                            <span className="detail-value">{typeof value === 'object' ? JSON.stringify(value) : String(value)}</span>
                          </div>
                        ))}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>
        ) : null;

      default:
        return <div>Select a tab</div>;
    }
  };

  if (loading) {
    return <div className="loading">Loading...</div>;
  }

  return (
    <>
      <AdminNavbar
        activeTab={activeTab}
        setActiveTab={setActiveTab}
        onLogout={handleLogout}
        adminRank={adminRank}
      />
      <div className="admin-content">
        {error && <div className="error-message">{error}</div>}
        {renderContent()}

        {/* Create Admin Modal */}
        {showCreateAdmin && (
          <div className="modal">
            <div className="modal-content">
              <h2>Create Admin Account</h2>
              <form onSubmit={handleCreateAdmin}>
                <div className="form-group">
                  <label>Username</label>
                  <input
                    type="text"
                    value={newAdmin.username}
                    onChange={(e) => setNewAdmin({ ...newAdmin, username: e.target.value })}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Rank</label>
                  <select
                    value={newAdmin.rank}
                    onChange={(e) => setNewAdmin({ ...newAdmin, rank: e.target.value })}
                    required
                  >
                    <option value="manager">Manager</option>
                    <option value="owner">Owner</option>
                  </select>
                </div>
                <div className="modal-actions">
                  <button type="button" onClick={() => setShowCreateAdmin(false)}>Cancel</button>
                  <button type="submit">Create Admin</button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Credentials Display Modal */}
        {createdAdminCredentials && (
          <div className="modal">
            <div className="modal-content">
              <h2>Admin Account Created</h2>
              <div className="credentials-display">
                <div className="form-group">
                  <label>Username:</label>
                  <code className="credential">{createdAdminCredentials.username}</code>
                </div>
                <div className="form-group">
                  <label>Password:</label>
                  <code className="credential">{createdAdminCredentials.password}</code>
                </div>
                <div className="form-group">
                  <label>Rank:</label>
                  <code className="credential">{createdAdminCredentials.rank}</code>
                </div>
                <p className="warning">
                  Please save these credentials securely. The password cannot be recovered later.
                </p>
              </div>
              <div className="modal-actions">
                <button 
                  onClick={() => setCreatedAdminCredentials(null)}
                  className="button-primary"
                >
                  I've Saved The Credentials
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default AdminPanel; 